import { shortDate } from '@/utils/report-date-group';
import dayjs from 'dayjs';

// Treat the literal string "Invalid Date" (and other unparseables) as missing.
// Some upstream date-picker paths dispatch dayjs(badInput).format() which
// yields the string "Invalid Date" rather than null. Without this guard,
// those values flow into compare/filter payloads and fail backend validation.
const sanitizeDateInput = (value) => {
  if (value === null || value === undefined) return undefined;
  if (typeof value === 'string') {
    if (value === 'Invalid Date' || value === 'NaN-NaN-NaN' || value === '')
      return undefined;
    if (shortDate[value] !== undefined) return value; // shortDate label
  }
  return dayjs(value).isValid() ? value : undefined;
};

export const getCompareTime = (timeFrame) => {
  const from = sanitizeDateInput(timeFrame?.from);
  const to = sanitizeDateInput(timeFrame?.to);
  if (!from) return null;
  if (!to) {
    const noOfDays = shortDate[from];
    if (noOfDays !== undefined) {
      // Known shortDate label (past7d, past30d, ...): existing behaviour.
      const toDate = dayjs().subtract(noOfDays + 1, 'day');
      const fromDate = dayjs(toDate).subtract(noOfDays, 'day');
      return {
        from: fromDate.format('YYYY-MM-DD'),
        to: toDate.format('YYYY-MM-DD'),
      };
    }
    // Absolute date saved without a `to` (filter runs `from` → today).
    // Anchor the compare window on `from` and shift back by its length —
    // same convention as the `to`-present branch below.
    const parsedFrom = dayjs(from);
    if (!parsedFrom.isValid()) return null;
    const diff = dayjs().diff(parsedFrom, 'day');
    if (!Number.isFinite(diff) || diff < 0) return null;
    const toDate = parsedFrom.subtract(1, 'day');
    const fromDate = toDate.subtract(diff, 'day');
    return {
      from: fromDate.format('YYYY-MM-DD'),
      to: toDate.format('YYYY-MM-DD'),
    };
  }
  const diff = dayjs(to).diff(from, 'day');
  const toDate = dayjs(from).subtract(1, 'day');
  const fromDate = dayjs(toDate).subtract(diff, 'day');
  const formattedCompareFrom = fromDate.format('YYYY-MM-DD');
  const formattedCompareTo = toDate.format('YYYY-MM-DD');
  return { from: formattedCompareFrom, to: formattedCompareTo };
};

export const getParams = ({
  timeFrame = {},
  group = {},
  filters = {},
  drilldown = {},
  compare = false,
}) => {
  try {
    const params = {
      filter_by: {
        date: {
          from: timeFrame?.from,
        },
        ...filters,
      },
      date_group: timeFrame?.date_group,
    };

    if (drilldown?.metric) {
      params['drilldown'] = drilldown;
    }

    if (timeFrame?.to) {
      params['filter_by']['date']['to'] = timeFrame?.to;
    }

    if (compare) {
      // Use the effective date sent to the server (post-filters merge), not raw
      // timeFrame. Saved reports can leave timeFrame and filters out of sync —
      // e.g. filters.date supplies a real range while timeFrame.to is null —
      // which previously yielded compare = "Invalid Date".
      const effectiveDate = params.filter_by?.date || {};
      const compareTime = getCompareTime({
        from: effectiveDate.from ?? timeFrame?.from,
        to: effectiveDate.to ?? timeFrame?.to,
      });
      if (compareTime) {
        params['compare'] = {
          date: { from: compareTime.from, to: compareTime.to },
        };
      }
    }
    if (group?.group_by_1) {
      params['group_by_1'] = group?.group_by_1?.id;
    }
    if (group?.group_by_2) {
      params['group_by_2'] = group?.group_by_2?.id;
    }

    return params;
  } catch (error) {
    console.error(error);
    return {};
  }
};
